import {
  ActionIcon,
  Box,
  Center,
  Loader,
  Modal,
  Pagination,
  Stack,
  Stepper,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import React, { useEffect, useMemo, useState } from "react";

import { notifications } from "@mantine/notifications";
import { IconDownload, IconEye } from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { getCsvExportForUser } from "api/invoice.service";
import { getOrders } from "api/order.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
  MSG_DEFAULT_FORM_DATA_LOAD_TITLE,
} from "common/constants";
import { formatDate } from "common/utils";
import { Order, Orders } from "types/model.types";
import OrderPreview from "./OrderPreview";

enum OrderStage {
  PICKING = "PICKING",
  PACKING = "PACKING",
  COMPLETE = "COMPLETE",
}
const OverviewPage: React.FC = () => {
  const apiFetchRef = React.useRef<boolean>(false);
  const [orders, setOrders] = useState<Orders>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [opened, { open, close }] = useDisclosure(false);

  const handlePreviewClick = (order: Order) => {
    setSelectedOrder(order);
    open();
  };

  const [selectedStage, setSelectedStage] = useState<OrderStage>(
    OrderStage.PICKING,
  );
  const stageSteps = ["PICKING", "PACKING", "COMPLETE"];
  const activeStep = stageSteps.indexOf(selectedStage);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const filteredOrders = orders.filter(
    (order) => order.orderStage === selectedStage,
  );
  //  const paginatedOrders = filteredOrders.slice(start, end);
  const paginatedOrders = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredOrders.slice(start, start + pageSize);
  }, [filteredOrders, page, pageSize]);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const response = await safeApiCall(getOrders());
        if (response) setOrders(response.data ?? []);
      } catch (error) {
        apiFetchRef.current = false;
        notifications.show({
          title: MSG_DEFAULT_FORM_DATA_LOAD_TITLE,
          message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
          color: MantineNotificationColor.Error,
          position: MantineNotificationPosition.TopCenter,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    return () => {
      mounted = false;
    };
  }, []);
  // Reset page when component mounts or when selected stage changes

  useEffect(() => {
    setPage(1); // Reset page when stage changes
  }, [selectedStage]);
  // Ensure current page is valid when filteredOrders change

  // - Guard page > maxPage just in case:
  //   If the filtered orders change and the current page exceeds the new max, reset to page 1
  // - This ensures that the pagination remains valid even if the data changes.
  useEffect(() => {
    const maxPage = Math.ceil(filteredOrders.length / pageSize);
    if (page > maxPage) setPage(1);
  }, [filteredOrders.length, pageSize]);

  const handleDownloadCsv = async (orderId: number) => {
    try {
      await safeApiCall(getCsvExportForUser(orderId));
      notifications.show({
        title: "File Download!",
        message: `Order ${orderId} CSV is downloading.`,
        color: MantineNotificationColor.Success,
        position: MantineNotificationPosition.TopCenter,
      });
    } catch (err) {
      notifications.show({
        title: "File Download!",
        message: `Unable to export CSV for this order. ${err}`,
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
    }
  };
  const rows = paginatedOrders.map((order) => (
    <Table.Tr key={order.orderId}>
      <Table.Td>{order.orderId}</Table.Td>
      <Table.Td>{formatDate(order.createdOn)}</Table.Td>
      <Table.Td>{order.yourOrderNo ?? order.customerOrderNo ?? "—"}</Table.Td>
      <Table.Td>{order.invoiceToDebtorName}</Table.Td>
      <Table.Td>{order.deliverToDebtorName}</Table.Td>
      <Table.Td>{order.orderStage}</Table.Td>
      <Table.Td>
        <Tooltip label="Preview Order">
          <ActionIcon onClick={() => handlePreviewClick(order)}>
            <IconEye size={24} />
          </ActionIcon>
        </Tooltip>
      </Table.Td>
      <Table.Td>
        <Tooltip label="Download CSV">
          <ActionIcon
            disabled={order.orderStage !== "COMPLETE"}
            onClick={() => handleDownloadCsv(order.orderId)}
          >
            <IconDownload size={24} />
          </ActionIcon>
        </Tooltip>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Box>
      <Stack>
        <Title order={2}>My Orders: {selectedStage}</Title>
        <Text size="sm" c="dimmed">
          Showing 30 items per page
        </Text>

        {loading ? (
          <Center>
            <Loader />
          </Center>
        ) : (
          <Table
            striped
            highlightOnHover
            withTableBorder
            style={{ minWidth: 740 }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th colSpan={7}>
                  <Stack mb="md" miw={740}>
                    <Center>
                      <Text size="sm" c="blue.6" ta="center" w={500}>
                        Tip: You can click each step to filter orders by stage!
                      </Text>
                    </Center>
                    <Stepper
                      active={activeStep}
                      onStepClick={(index) =>
                        setSelectedStage(stageSteps[index] as OrderStage)
                      }
                      allowNextStepsSelect
                      color="blue"
                    >
                      <Stepper.Step
                        label="Picking"
                        description="Order is being picked"
                      />
                      <Stepper.Step
                        label="Packing"
                        description="Order is being packed"
                      />
                      <Stepper.Step
                        label="Complete"
                        description="Order has shipped"
                      />
                    </Stepper>
                  </Stack>
                </Table.Th>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Order Number</Table.Th>
                <Table.Th>Date</Table.Th>
                <Table.Th>Customer Order No</Table.Th>
                <Table.Th>Invoice To</Table.Th>
                <Table.Th>Deliver To</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Details</Table.Th>
                <Table.Th>File</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredOrders.length === 0 ? (
                <Table.Tr>
                  <Table.Th colSpan={8}>
                    <Text>No orders found matching specified filter.</Text>
                  </Table.Th>
                </Table.Tr>
              ) : (
                rows
              )}
            </Table.Tbody>
            <Table.Tfoot>
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Center>
                    {/* Right: Pagination */}
                    <Pagination
                      total={Math.ceil(filteredOrders.length / pageSize)}
                      value={page}
                      onChange={setPage}
                    />
                  </Center>
                </Table.Td>
              </Table.Tr>
            </Table.Tfoot>
          </Table>
        )}
      </Stack>
      <Modal opened={opened} onClose={close} fullScreen title="Order Preview">
        {selectedOrder && <OrderPreview order={selectedOrder} />}
      </Modal>
    </Box>
  );
};

export default OverviewPage;
